import {DataTable} from "../DataTable/DataTable";
import {useTranslation} from "react-i18next";
import {Box, Modal, ModalProps} from "@mantine/core";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {MouseEvent, useCallback, useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import {sortBy} from "lodash";
import {EntityActionGroup} from "../../group/EntityActionGroup/EntityActionGroup";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {ServiceEventTypeServiceTypeEntity} from "@ots-frontend/ots-data";
import {useServiceEventTypeServiceTypeDataTableFilter} from "./useServiceEventTypeServiceTypeDataTableFilter";
import {useServiceEventTypeServiceTypeMutation} from "../../../hooks/useServiceEventTypeServiceTypeMutation";

const EMPTY_ARRAY: ServiceEventTypeServiceTypeEntity[] = []

export type ServiceEventTypeDataTableProps = EntityDataTableProps<ServiceEventTypeServiceTypeEntity>

export const ServiceEventTypeServiceTypeDataTable = (props: ServiceEventTypeDataTableProps) => {
  const {
    records: roles = EMPTY_ARRAY,
    recordsPerPageOptions,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = useServiceEventTypeServiceTypeDataTableFilter({
    records: roles
  })

  const sortConfig = useMemo(() => {
    return {
      "serviceEventType": {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.serviceEventType?.code?.toLowerCase()
          }])
        }
      },
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'serviceEventType', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const {
    deleteServiceEventTypeServiceType,
  } = useServiceEventTypeServiceTypeMutation()

  const onDeleteActionClick = useCallback((e: MouseEvent<HTMLButtonElement>, record: ServiceEventTypeServiceTypeEntity) => {
    e.stopPropagation()
    deleteServiceEventTypeServiceType(record.id, undefined)
  }, [deleteServiceEventTypeServiceType])

  const columns = useMemo(() => {
    const values: DataTableColumn<ServiceEventTypeServiceTypeEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "serviceEventType"),
        title: t("common.entity.serviceeventtypeservicetype.table.column.serviceeventtype", "Event Type"),
        accessor: 'serviceEventType',
        width: 400,
        sortable: true,
        render: ({serviceEventType}) => (
          <FormattedDatabaseCode value={serviceEventType}/>
        ),
      },
      {
        title: '',
        accessor: "actions",
        width: 100,
        textAlignment: 'right',
        render: (company) => (
          <EntityActionGroup
            permissionPrefix={"servicetype"}
            view={false}
            edit={false}
            delete={true}
            onDeleteActionClick={(e) => onDeleteActionClick(e, company)}
          />
        )
      }
    ]

    return values
  }, [filterConfig, onDeleteActionClick, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type ServiceEventTypeDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<ServiceEventTypeDataTableProps, 'recordsPerPageOptions'>
}

export const ServiceEventTypeDataTableModal = (props: ServiceEventTypeDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.serviceeventtypeservicetype.plural", "Companies"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <ServiceEventTypeServiceTypeDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
